import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StoragePreviewsService, StoragePreviewResponse, StoragePreviewPageResponse } from '@core/services/previews/storage-previews.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';

import { AmsFile } from '@core/objects/ams-file';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';

/**
 * StorageGridPreviewComponent
 * 
 * Manages and displays image previews for a file in a grid format with pagination.
 * This component:
 * - Fetches the list of available preview pages with presigned URLs via StoragePreviewsService
 * - Manages pagination (previous/next page navigation)
 * - Uses StoragePagePreviewComponent to display each page
 * - Intelligently preloads nearby pages for better UX
 * 
 * API Flow:
 * 1. Service calls: GET /pub/p/{uuid}/img?signature={signature}&size={size}
 * 2. API returns array of pages with presigned URLs: https://download.twonas.com/previews/{page}.jpg?X-Amz-Signature=...
 * 3. Presigned URLs are passed to StoragePagePreviewComponent for display
 * 4. Presigned URLs expire after 5 minutes
 * 5. If signature expires, parent component uses SignedFilesListComponent/FileSignatureManagerService to reload
 * 
 * Retry Logic:
 * - The service retries on 425 (Too Early) errors with 10 attempts and 3-second delay
 * - No automatic background refresh - only retries when user actions require it
 * 
 * It works with AmsFile objects that contain:
 * - uuid: The unique identifier of the file
 * - access: Object containing the cryptographic signature for authorization
 */
@Component({
    selector: 'core-storage-grid-preview',
    templateUrl: './grid-preview.component.html',
    styleUrls: ['./grid-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StorageGridPreviewComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * The AmsFile object containing uuid and access information
     */
    @Input() file: AmsFile;

    /**
     * Image preview quality/resolution strings
     */
    @Input() size: 'min' | 'small' | 'medium' | 'big' = 'small';

    /**
     * Number of extra pages to preload around current page
     * @deprecated Only showing first page in grid view. Full pagination available in full-page preview.
     * @default 0 none
     */
    @Input() preloadPages: number = 0;

    /**
     * Whether the preview is currently displayed/expanded
     */
    @Input() displayed: boolean = false;

    /**
     * Number of notes associated with the file
     */
    @Input() notes: number | null = null;

    /**
     * Menu items for the three dots menu
     */
    @Input() menu: Array<ThreeDotsMenuItem> = [];

    /**
     * Emitted when a preview page successfully loads
     */
    @Output() onPageLoad = new EventEmitter<number>();

    /**
     * Emitted when a preview page fails to load
     */
    @Output() onPageError = new EventEmitter<{ page: number; error: any }>();

    /**
     * Emitted when file is clicked (typically to open/expand)
     */
    @Output() actionClick = new EventEmitter<AmsFile>();

    /**
     * Emitted when a menu item is selected
     */
    @Output() menuClick = new EventEmitter<AmsFilesListItemMenuClick>();

    /**
     * Emitted when the download icon is clicked
     */
    @Output() iconDownloadClick = new EventEmitter<AmsFile>();

    /**
     * Emitted when a 403 Forbidden error occurs (signature expired/invalid)
     * Parent component should refresh file signatures and reload the file
     */
    @Output() signatureRefreshNeeded = new EventEmitter<void>();

    /**
     * Current page being displayed (1-based)
     */
    public currentPage: number = 1;

    /**
     * Total number of pages available
     */
    public totalPages: number = 0;

    /**
     * List of available preview pages with presigned URLs from the API
     */
    public previewPages: StoragePreviewPageResponse[] = [];

    /**
     * Indicates if the preview list is currently loading
     */
    public isLoading: boolean = true;

    /**
     * Error message if preview fetch failed
     */
    public errorMessage: string | null = null;

    /**
     * Track the error status code for error-specific handling
     */
    public lastErrorStatus: number | null = null;

    /**
     * Track which pages have been successfully loaded
     */
    public loadedPages = new Set<number>();

    private destroy$ = new Subject<void>();

    constructor(
        private storagePreviewsService: StoragePreviewsService,
        private signatureManager: FileSignatureManagerService,
        private changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        if (this.file && this.file.uuid && this.file.access?.signature) {
            // Check if signature has expired and keep loading status meanwhile.
            if (this.signatureManager.isSignatureExpired(this.file)) {
                this.signatureRefreshNeeded.emit();
                this.changeDetectorRef.markForCheck();
            } else {
                this.fetchPreviewPages();
            }
        } else {
            this.errorMessage = 'Invalid file or missing signature';
            this.isLoading = false;
            this.changeDetectorRef.markForCheck();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Only handle actual file changes AFTER initialization
        // Initial load is handled by ngOnInit to avoid duplicate fetches
        if (changes.file && !changes.file.firstChange) {
            const newFile: AmsFile = changes.file.currentValue;
            const previousFile: AmsFile = changes.file.previousValue;

            // Check if it's a different file OR same file with updated signature
            if (newFile.uuid !== previousFile.uuid || newFile.access?.signature !== previousFile.access?.signature) {
                // Reset state for new file
                this.currentPage = 1;
                this.totalPages = 0;
                this.previewPages = [];
                this.loadedPages.clear();
                this.lastErrorStatus = null;

                // Re-fetch preview pages with fresh signature
                if (newFile && newFile.uuid && newFile.access?.signature) {
                    this.fetchPreviewPages();
                } else {
                    this.errorMessage = 'Invalid file or missing signature';
                    this.isLoading = false;
                    this.changeDetectorRef.markForCheck();
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Fetches the list of available preview pages via StoragePreviewsService
     * Implements retry logic with 10 attempts and 3-second delay (30 seconds total)
     * Also sets up automatic refresh before URL expiration (5-minute lifetime)
     */
    private fetchPreviewPages(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.storagePreviewsService.getPreviewPages(this.file, this.size)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: StoragePreviewResponse) => this.onPreviewListLoaded(response),
                error: (error: any) => this.onPreviewListError(error)
            });
    }

    /**
     * Handles successful preview list fetch
     */
    private onPreviewListLoaded(response: StoragePreviewResponse): void {
        this.previewPages = response.pages || [];
        this.totalPages = this.previewPages.length;
        this.isLoading = false;
        this.errorMessage = '';

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Handles preview list fetch error
     * 
     * Behavior by status code:
     * - 403 (Forbidden): Keep loading state visible while parent refreshes signatures
     * - 404 (Not Found): Show error message to user
     * - 425 (Too Early): Show error after retry attempts exhausted
     * - 500 (Server Error): Show error message
     */
    private onPreviewListError(error: any): void {
        this.lastErrorStatus = error.status || null;

        // Extract error code from response
        const errorCode = error.error?.code || error.error?.error?.code;

        // Handle specific API error codes
        if (error.status === 403) {
            // Signature expired - keep loading spinner visible while parent refreshes files
            // Don't set isLoading = false, so the loading spinner continues showing
            this.isLoading = true;
            this.errorMessage = 'Access denied - signature may have expired'; // For debugging only
            // Signal parent to refresh signatures
            this.signatureRefreshNeeded.emit();
        } else if (error.status === 404) {
            // File or preview not available - show error to user
            this.isLoading = false;
            // Check if file genuinely has no preview
            if (errorCode === 'preview_images_not_exists') {
                this.errorMessage = 'This file type has no preview available (e.g., archive, compressed file).';
            } else {
                this.errorMessage = 'File or preview generation failed. Click retry to try again.';
            }
        } else if (error.status === 425) {
            // Preview generation timeout - show error to user
            this.isLoading = false;
            this.errorMessage = 'Preview generation in progress. Maximum retry attempts exceeded. Please try again later.';
        } else if (error.status === 500) {
            // Server error - show error to user
            this.isLoading = false;
            this.errorMessage = 'Preview service unavailable. Please try again later.';
            // Signal parent to refresh signatures
            this.signatureRefreshNeeded.emit();
        } else if (error.message) {
            this.isLoading = false;
            this.errorMessage = error.message;
        } else {
            this.isLoading = false;
            this.errorMessage = 'Failed to load preview pages';
        }

        this.changeDetectorRef.markForCheck();
    }

    /**
     * Retry loading preview pages
     * Used when 404 error occurs to allow user to manually retry
     */
    public retryLoadPreview(): void {
        this.lastErrorStatus = null;
        this.fetchPreviewPages();
    }

    /**
     * Navigate to the next page
     * @deprecated Pagination removed from grid view. Use full-page preview for multi-page navigation.
     * Functionality kept for future use if pagination is re-enabled.
     * Last updated: 2026-06-17
     */
    // nextPage(event?: Event): void {
    //     if (event) {
    //         event.stopPropagation();
    //     }
    //     if (this.currentPage < this.totalPages) {
    //         this.currentPage++;
    //         this.changeDetectorRef.markForCheck();
    //     }
    // }

    /**
     * Navigate to the previous page
     * @deprecated Pagination removed from grid view. Use full-page preview for multi-page navigation.
     * Functionality kept for future use if pagination is re-enabled.
     * Last updated: 2026-06-17
     */
    // previousPage(event?: Event): void {
    //     if (event) {
    //         event.stopPropagation();
    //     }
    //     if (this.currentPage > 1) {
    //         this.currentPage--;
    //         this.changeDetectorRef.markForCheck();
    //     }
    // }

    /**
     * Check if a page should be visible (current page only)
     * @deprecated Pagination removed from grid view. Only first page (page 1) is visible.
     * Functionality kept for future use if pagination is re-enabled.
     * Last updated: 2026-06-17
     */
    isPageVisible(pageIndex: number): boolean {
        return (pageIndex) === this.currentPage;
    }

    /**
     * Check if a page should be preloaded
     * @deprecated Pagination removed from grid view. Only first page is preloaded.
     * Functionality kept for future use if pagination is re-enabled.
     * Last updated: 2026-06-17
     */
    shouldPreloadPage(pageIndex: number): boolean {
        const pageNumber = pageIndex; // pageIndex is 1-based from the response
        const distanceFromCurrent = Math.abs(pageNumber - this.currentPage);
        return distanceFromCurrent <= this.preloadPages;
    }

    /**
     * Get the presigned URL for a specific page
     */
    getPagePresignedUrl(pageIndex: number): string {
        if (pageIndex >= 0 && pageIndex < this.previewPages.length) {
            return this.previewPages[pageIndex].image.url;
        }
        return '';
    }

    /**
     * Get the page position (1-based)
     */
    getPagePosition(pageIndex: number): number {
        if (pageIndex >= 0 && pageIndex < this.previewPages.length) {
            return this.previewPages[pageIndex].position;
        }
        return pageIndex + 1;
    }

    /**
     * Handle page load success
     */
    onStoragePageLoad(pageIndex: number): void {
        this.loadedPages.add(pageIndex);
        this.onPageLoad.emit(pageIndex);
    }

    /**
     * Handle page load error
     */
    onStoragePageError(pageIndex: number, error: any): void {
        this.onPageError.emit({ page: pageIndex, error });
    }

    /**
     * Check if navigation buttons should be disabled
     */
    isNextButtonDisabled(): boolean {
        return this.currentPage >= this.totalPages;
    }

    isBackButtonDisabled(): boolean {
        return this.currentPage <= 1;
    }

    /**
     * Get human-readable page info
     */
    getPageInfo(): string {
        return `${this.currentPage} / ${this.totalPages}`;
    }

    /**
     * Format file size to human-readable format (B, kB, MB, GB, TB)
     */
    humanFileSize(size: number): string {
        if (!size) return '0 B';
        const i = Math.floor(Math.log(size) / Math.log(1024));
        return +(size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    /**
     * Extract file extension from filename
     */
    getFileExtension(filename: string): string {
        if (!filename || !filename.includes('.')) {
            return '';
        }
        return filename.split('.').pop() || '';
    }

    /**
     * Handle menu item selection
     */
    onMenuSelect(key: string): void {
        this.menuClick.emit({ key: key, file: this.file });
    }

    /**
     * Handle download icon click
     */
    onActionDownloadClick(event: Event): void {
        this.iconDownloadClick.emit(this.file);
    }

    /**
     * Handle file action click (open/expand)
     */
    onActionClick(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.actionClick.emit(this.file);
    }
}
